import { Suspense } from 'react';
import { Routes, Route, Navigate, Outlet, useLocation } from 'react-router';
import { AppLayout } from '@/layouts/app-layout';
import { SettingsLayout } from '@/layouts/settings-layout';
import { SuperadminLayout } from '@/app/superadmin/_components/superadmin-layout';
import { SuperadminAuthProvider } from '@/contexts/superadmin-auth-context';
import { lazyWithRetry } from '@/lib/lazy-with-retry';

// Lazy-loaded pages
const HomePage = lazyWithRetry(() => import('@/app/page'));
const DashboardPage = lazyWithRetry(() => import('@/app/dashboard/page'));
const LoginPage = lazyWithRetry(() => import('@/app/login/page'));
const LoginCompletePage = lazyWithRetry(() => import('@/app/login/complete/page'));
const LoginInvitePage = lazyWithRetry(() => import('@/app/login/invite/page'));
const LoginTenantPage = lazyWithRetry(() => import('@/app/login/tenant/page'));
const LocalRunnerAuthorizePage = lazyWithRetry(() => import('@/app/local-runner/authorize/page'));
const SignupInvitedPage = lazyWithRetry(() => import('@/app/signup/invited/page'));
const OnboardingPage = lazyWithRetry(() => import('@/app/onboarding/page'));
const VerifyEmailPage = lazyWithRetry(() => import('@/app/verify-email/page'));
const ResetPasswordRequestPage = lazyWithRetry(() => import('@/app/reset-password-request/page'));
const ResetPasswordPage = lazyWithRetry(() => import('@/app/reset-password/page'));
const OrganizationsPage = lazyWithRetry(() => import('@/app/organizations/page'));
const TenantsPage = lazyWithRetry(() => import('@/app/tenants/page'));
const InvitationsPage = lazyWithRetry(() => import('@/app/invitations/page'));
const InviteTokenPage = lazyWithRetry(() => import('@/app/invite/[token]/page'));
const InvitePlatformTokenPage = lazyWithRetry(() => import('@/app/invite/platform/[token]/page'));
const TalkPage = lazyWithRetry(() => import('@/app/talk/[token]/page'));

// App pages
const EntitiesPage = lazyWithRetry(() => import('@/app/entities/page'));
const EntityPage = lazyWithRetry(() => import('@/app/entities/[name]/page'));
const EntityRecordPage = lazyWithRetry(() => import('@/app/entities/[name]/[id]/page'));
const EntityNewPage = lazyWithRetry(() => import('@/app/entities/[name]/new/page'));
const EntityVersionsPage = lazyWithRetry(() => import('@/app/entities/[name]/versions/page'));
const ChannelsPage = lazyWithRetry(() => import('@/app/channels/page'));
const IntegrationsPage = lazyWithRetry(() => import('@/app/integrations/page'));
const IntegrationDetailPage = lazyWithRetry(() => import('@/app/integrations/[slug]/page'));
const AgentsPage = lazyWithRetry(() => import('@/app/agents/page'));
const AgentNewPage = lazyWithRetry(() => import('@/app/agents/new/page'));
const AgentEditPage = lazyWithRetry(() => import('@/app/agents/[id]/page'));
const AgentTestPage = lazyWithRetry(() => import('@/app/agents/[id]/test/page'));
const TasksPage = lazyWithRetry(() => import('@/app/tasks/page'));
const AgentCommunicationsPage = lazyWithRetry(() => import('@/app/agents/communications/page'));
const AgentConnectionsPage = lazyWithRetry(() => import('@/app/agents/[id]/connections/page'));
const AgentBehaviorPage = lazyWithRetry(() => import('@/app/agents/[id]/behavior/page'));
const AgentRitualsPage = lazyWithRetry(() => import('@/app/agents/[id]/rituals/page'));
const AgentMemoryPage = lazyWithRetry(() => import('@/app/agents/[id]/memory/page'));
import { AgentLayout } from '@/app/agents/[id]/layout';
const AgentRulesPage = lazyWithRetry(() => import('@/app/agents/[slug]/rules/page'));
const AgentLogsPage = lazyWithRetry(() => import('@/app/agents/[slug]/logs/page'));
const RolesPage = lazyWithRetry(() => import('@/app/roles/page'));
const UsersPage = lazyWithRetry(() => import('@/app/users/page'));
const GeneratePage = lazyWithRetry(() => import('@/app/generate/page'));
const KnowledgePage = lazyWithRetry(() => import('@/app/knowledge/page'));
const ActivityPage = lazyWithRetry(() => import('@/app/activity/page'));
const LogsPage = lazyWithRetry(() => import('@/app/logs/page'));
const AppsPage = lazyWithRetry(() => import('@/app/apps/page'));
const FysoTeamsPage = lazyWithRetry(() => import('@/app/apps/fyso-teams/page'));
const SitesPage = lazyWithRetry(() => import('@/app/sites/page'));
const ApiExplorerPage = lazyWithRetry(() => import('@/app/api-explorer/page'));
const UsagePage = lazyWithRetry(() => import('@/app/usage/page'));
// BillingPage removed — /billing redirects to /org/billing
const InstallSlugPage = lazyWithRetry(() => import('@/app/install/[slug]/page'));
const ViewsPage = lazyWithRetry(() => import('@/app/views/page'));
const ViewRecordsPage = lazyWithRetry(() => import('@/app/views/[slug]/page'));
const MessagesPage = lazyWithRetry(() => import('@/app/messages/page'));

// Settings pages
const SettingsGeneralPage = lazyWithRetry(() => import('@/app/settings/general/page'));
const SettingsProfilePage = lazyWithRetry(() => import('@/app/settings/profile/page'));
const SettingsApiKeysPage = lazyWithRetry(() => import('@/app/settings/api-keys/page'));
const SettingsEntitiesPage = lazyWithRetry(() => import('@/app/settings/entities/page'));
const SettingsEntityPage = lazyWithRetry(() => import('@/app/settings/entities/[name]/page'));
const SettingsEntityNewPage = lazyWithRetry(() => import('@/app/settings/entities/new/page'));
const SettingsRulesPage = lazyWithRetry(() => import('@/app/settings/rules/page'));
const SettingsRuleEditPage = lazyWithRetry(() => import('@/app/settings/rules/[entityName]/[ruleId]/page'));
const SettingsRulesExecutionLogsPage = lazyWithRetry(() => import('@/app/settings/rules/execution-logs/page'));
const SettingsTemplatesPage = lazyWithRetry(() => import('@/app/settings/templates/page'));
const SettingsWebhooksPage = lazyWithRetry(() => import('@/app/settings/webhooks/page'));
const SettingsMcpPage = lazyWithRetry(() => import('@/app/settings/mcp/page'));
const SettingsMcpProfilesPage = lazyWithRetry(() => import('@/app/settings/mcp-profiles/page'));
const SettingsDedicatedPage = lazyWithRetry(() => import('@/app/settings/dedicated/page'));
const SettingsEmailLogsPage = lazyWithRetry(() => import('@/app/settings/email-logs/page'));
const SettingsRealtimePage = lazyWithRetry(() => import('@/app/settings/realtime/page'));
const SettingsAiProviderPage = lazyWithRetry(() => import('@/app/settings/ai-provider/page'));
const SettingsAiCostPage = lazyWithRetry(() => import('@/app/settings/ai-cost/page'));
const SettingsAiTemplatesPage = lazyWithRetry(() => import('@/app/settings/ai-templates/page'));
const SettingsIntegrationsPage = lazyWithRetry(() => import('@/app/settings/integrations/page'));
const SettingsRuleNewPage = lazyWithRetry(() => import('@/app/settings/rules/[entityName]/new/page'));

// Org pages
const OrgMembersPage = lazyWithRetry(() => import('@/app/org/members/page'));
const OrgBillingPage = lazyWithRetry(() => import('@/app/org/billing/page'));
const OrgSettingsPage = lazyWithRetry(() => import('@/app/org/settings/page'));
const OrgInviteAcceptPage = lazyWithRetry(() => import('@/app/signup/org-invite/page'));

// Superadmin pages
const SuperadminOverviewPage = lazyWithRetry(() => import('@/app/superadmin/overview/page'));
const SuperadminLoginPage = lazyWithRetry(() => import('@/app/superadmin/login/page'));
const SuperadminTenantsPage = lazyWithRetry(() => import('@/app/superadmin/tenants/page'));
const SuperadminUsersPage = lazyWithRetry(() => import('@/app/superadmin/users/page'));
const SuperadminInvitationsPage = lazyWithRetry(() => import('@/app/superadmin/invitations/page'));
const SuperadminHealthPage = lazyWithRetry(() => import('@/app/superadmin/health/page'));
const SuperadminOrganizationsPage = lazyWithRetry(() => import('@/app/superadmin/organizations/page'));
const SuperadminBillingPage = lazyWithRetry(() => import('@/app/superadmin/billing/page'));
const SuperadminAppsPage = lazyWithRetry(() => import('@/app/superadmin/apps/page'));
const SuperadminEntitlementsPage = lazyWithRetry(() => import('@/app/superadmin/entitlements/page'));
const SuperadminPaddleWebhooksPage = lazyWithRetry(() => import('@/app/superadmin/paddle-webhooks/page'));
const SuperadminOperationsPage = lazyWithRetry(() => import('@/app/superadmin/operations/page'));
const SuperadminSecurityPage = lazyWithRetry(() => import('@/app/superadmin/security/page'));

// Platform pages
const PlatformInvitationsPage = lazyWithRetry(() => import('@/app/platform/invitations/page'));

// Static pages
const OAuthConsentPage = lazyWithRetry(() => import('@/app/oauth/consent/page'));
const PrivacyPage = lazyWithRetry(() => import('@/app/privacy/page'));
const TermsPage = lazyWithRetry(() => import('@/app/terms/page'));
const CookiesPage = lazyWithRetry(() => import('@/app/cookies/page'));
const RefundPage = lazyWithRetry(() => import('@/app/refund/page'));

function AgentEditRedirect() {
  const location = useLocation();
  return <Navigate to={`../advanced${location.search}${location.hash}`} relative="path" replace />;
}

function PageLoader() {
  return (
    <div className="flex h-full items-center justify-center">
      <div className="h-8 w-8 animate-spin rounded-full border-4 border-primary border-t-transparent" />
    </div>
  );
}

export function AppRouter() {
  return (
    <Suspense fallback={<PageLoader />}>
      <Routes>
        {/* Auth routes (no app shell) */}
        <Route path="/" element={<HomePage />} />
        <Route path="/login" element={<LoginPage />} />
        <Route path="/login/complete" element={<LoginCompletePage />} />
        <Route path="/login/invite" element={<LoginInvitePage />} />
        <Route path="/login/tenant" element={<LoginTenantPage />} />
        <Route path="/local-runner/authorize" element={<LocalRunnerAuthorizePage />} />
        <Route path="/signup/invited" element={<SignupInvitedPage />} />
        <Route path="/signup/org-invite" element={<Suspense><OrgInviteAcceptPage /></Suspense>} />
        <Route path="/onboarding" element={<OnboardingPage />} />
        <Route path="/verify-email" element={<VerifyEmailPage />} />
        <Route path="/reset-password-request" element={<ResetPasswordRequestPage />} />
        <Route path="/reset-password" element={<ResetPasswordPage />} />
        <Route path="/organizations" element={<OrganizationsPage />} />
        <Route path="/tenants" element={<TenantsPage />} />
        <Route path="/invitations" element={<InvitationsPage />} />
        <Route path="/invite/:token" element={<InviteTokenPage />} />
        <Route path="/invite/platform/:token" element={<InvitePlatformTokenPage />} />
        <Route path="/talk/:token" element={<TalkPage />} />
        <Route path="/privacy" element={<PrivacyPage />} />
        <Route path="/terms" element={<TermsPage />} />
        <Route path="/cookies" element={<CookiesPage />} />
        <Route path="/refund" element={<RefundPage />} />

        {/* App routes (with shell: TopNav + SideNav) */}
        <Route element={<AppLayout />}>
          <Route path="/oauth/consent" element={<OAuthConsentPage />} />
          <Route path="/agents/communications" element={<AgentCommunicationsPage />} />
          <Route path="/tasks" element={<TasksPage />} />
          <Route path="/dashboard" element={<DashboardPage />} />
          <Route path="/entities" element={<EntitiesPage />} />
          <Route path="/entities/:name" element={<EntityPage />} />
          <Route path="/entities/:name/new" element={<EntityNewPage />} />
          <Route path="/entities/:name/versions" element={<EntityVersionsPage />} />
          <Route path="/entities/:name/:id" element={<EntityRecordPage />} />
          <Route path="/channels" element={<ChannelsPage />} />
          <Route path="/integrations" element={<IntegrationsPage />} />
          <Route path="/integrations/:slug" element={<IntegrationDetailPage />} />
          <Route path="/agents" element={<AgentsPage />} />
          {/* The former Beta workspace is now the normal agents view with an optional theme. */}
          <Route path="/beta/agents" element={<Navigate to="/agents" replace />} />
          <Route path="/agents/new" element={<AgentNewPage />} />
          {/* /agents/:id and the old "Editar" tab keep working: they redirect. */}
          <Route path="/agents/:id" element={<AgentLayout />}>
            <Route index element={<Navigate to="behavior" replace />} />
            <Route path="edit" element={<AgentEditRedirect />} />
            <Route path="advanced" element={<AgentEditPage />} />
            <Route path="test" element={<AgentTestPage />} />
            <Route path="connections" element={<AgentConnectionsPage />} />
            <Route path="behavior" element={<AgentBehaviorPage />} />
            <Route path="rituals" element={<AgentRitualsPage />} />
            <Route path="memory" element={<AgentMemoryPage />} />
          </Route>
          <Route path="/agents/:slug/rules" element={<AgentRulesPage />} />
          <Route path="/agents/:slug/logs" element={<AgentLogsPage />} />
          <Route path="/roles" element={<RolesPage />} />
          <Route path="/users" element={<UsersPage />} />
          <Route path="/generate" element={<GeneratePage />} />
          <Route path="/knowledge" element={<KnowledgePage />} />
          <Route path="/activity" element={<ActivityPage />} />
          <Route path="/logs" element={<LogsPage />} />
          <Route path="/apps" element={<AppsPage />} />
          <Route path="/apps/fyso-teams" element={<FysoTeamsPage />} />
          <Route path="/sites" element={<SitesPage />} />
          <Route path="/api-explorer" element={<ApiExplorerPage />} />
          <Route path="/usage" element={<UsagePage />} />
          <Route path="/billing" element={<Navigate to="/org/billing" replace />} />
          <Route path="/org/members" element={<Suspense><OrgMembersPage /></Suspense>} />
          <Route path="/org/billing" element={<Suspense><OrgBillingPage /></Suspense>} />
          <Route path="/org/settings" element={<Suspense><OrgSettingsPage /></Suspense>} />
          <Route path="/views" element={<ViewsPage />} />
          <Route path="/views/:slug" element={<ViewRecordsPage />} />
          <Route path="/messages" element={<MessagesPage />} />
          <Route path="/install/:slug" element={<InstallSlugPage />} />

          {/* Settings (guarded layout) */}
          <Route element={<SettingsLayout />}>
            <Route path="/settings" element={<Navigate to="/settings/general" replace />} />
            <Route path="/settings/general" element={<SettingsGeneralPage />} />
            <Route path="/settings/profile" element={<SettingsProfilePage />} />
            <Route path="/settings/api-keys" element={<SettingsApiKeysPage />} />
            <Route path="/settings/entities" element={<SettingsEntitiesPage />} />
            <Route path="/settings/entities/new" element={<SettingsEntityNewPage />} />
            <Route path="/settings/entities/:name" element={<SettingsEntityPage />} />
            <Route path="/settings/rules" element={<SettingsRulesPage />} />
            <Route path="/settings/rules/execution-logs" element={<SettingsRulesExecutionLogsPage />} />
            <Route path="/settings/rules/:entityName/new" element={<SettingsRuleNewPage />} />
            <Route path="/settings/rules/:entityName/:ruleId" element={<SettingsRuleEditPage />} />
            <Route path="/settings/templates" element={<SettingsTemplatesPage />} />
            <Route path="/settings/webhooks" element={<SettingsWebhooksPage />} />
            <Route path="/settings/mcp" element={<SettingsMcpPage />} />
            <Route path="/settings/mcp-profiles" element={<SettingsMcpProfilesPage />} />
            <Route path="/settings/dedicated" element={<SettingsDedicatedPage />} />
            <Route path="/settings/email-logs" element={<SettingsEmailLogsPage />} />
            <Route path="/settings/realtime" element={<SettingsRealtimePage />} />
            <Route path="/settings/ai-provider" element={<SettingsAiProviderPage />} />
            <Route path="/settings/ai-cost" element={<SettingsAiCostPage />} />
            <Route path="/settings/ai-templates" element={<SettingsAiTemplatesPage />} />
            <Route path="/settings/integrations" element={<SettingsIntegrationsPage />} />
          </Route>


          {/* Platform */}
          <Route path="/platform/invitations" element={<PlatformInvitationsPage />} />
        </Route>

        <Route element={<SuperadminAuthProvider><Outlet /></SuperadminAuthProvider>}>
          <Route path="/superadmin/login" element={<SuperadminLoginPage />} />

          {/* Superadmin */}
          <Route element={<SuperadminLayout />}>
            <Route path="/superadmin/overview" element={<SuperadminOverviewPage />} />
            <Route path="/superadmin/organizations" element={<SuperadminOrganizationsPage />} />
            <Route path="/superadmin/billing" element={<SuperadminBillingPage />} />
            <Route path="/superadmin/apps" element={<SuperadminAppsPage />} />
            <Route path="/superadmin/entitlements" element={<SuperadminEntitlementsPage />} />
            <Route path="/superadmin/paddle-webhooks" element={<SuperadminPaddleWebhooksPage />} />
            <Route path="/superadmin/operations" element={<SuperadminOperationsPage />} />
            <Route path="/superadmin/security" element={<SuperadminSecurityPage />} />
            <Route path="/superadmin/tenants" element={<SuperadminTenantsPage />} />
            <Route path="/superadmin/users" element={<SuperadminUsersPage />} />
            <Route path="/superadmin/invitations" element={<SuperadminInvitationsPage />} />
            <Route path="/superadmin/health" element={<SuperadminHealthPage />} />
          </Route>
        </Route>

        {/* Catch-all */}
        <Route path="*" element={<Navigate to="/dashboard" replace />} />
      </Routes>
    </Suspense>
  );
}
